/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import moment from 'moment';
import { MRT_ColumnDef } from 'material-react-table';
import { Box, IconButton, Switch } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

import {
  DateFormat,
  DayOfWeek,
  FiltersOfAllJobs,
  JobType,
  ScheduleTypes,
  states,
} from '../../../../Shared/Constants/App';
import { formatDateCell } from '../../Utilities/Jobs.Utils';
import { IJobs } from '../../../Jobs/Models/JobsView.Model';
import { IJobOverview } from '../../../Jobs/Components/JobsListView/JobsListView';
import { TaskData } from '../../../Jobs/Components/WorkTasks/WorkTasks';
import { IBidList } from '../../../Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import { formatTimeTo12Hr, getMRTTableHeadStyles } from '../../../../Shared/Utilities/utils';
import { isEmpty } from 'lodash';
import { dayNames, getWeekName, IOpenPosition, monthNames, ordinalSuffix, ScheduleData } from './AllSchedules.Model';
import { ColumnHeader } from './AllJobs.Style';
import theme from '../../../../Shared/Themes/theme';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getReadableScheduleDays = (scheduleType: string | undefined, scheduleDays: string[] | string): string => {
  const values = typeof scheduleDays === 'string' ? scheduleDays.split(',') : scheduleDays;
  if (!values || values.length === 0) return '-';

  switch (scheduleType) {
    case ScheduleTypes.Daily:
      return 'Every day';

    case ScheduleTypes.Weekly:
    case ScheduleTypes.Fortnightly:
      return values.map((dayIndex) => DayOfWeek[Number(dayIndex)]).join(', ');

    case ScheduleTypes.Monthly:
    case ScheduleTypes.BiMonthly:
      return values
        .map((val) => {
          if (val.startsWith('W')) {
            const match = val.match(/W(\d+)-(\d+)/);
            if (match) {
              const [, week, day] = match;
              return `${ordinalSuffix(Number(week))} week ${dayNames[Number(day)]} of every month`;
            }
          } else if (val.startsWith('M-')) {
            return val
              .split(',')
              .map((v) => `Every month on the ${ordinalSuffix(Number(v.replace('M-', '')))}`)
              .join(' and ');
          }
          return val;
        })
        .join(', ');

    case ScheduleTypes.Annual:
      return values
        .map((val) => {
          const [month, day] = val.replace('M', '').split('-');
          return `Every year on ${monthNames[Number(month) - 1]} ${ordinalSuffix(Number(day))}`;
        })
        .join(', ');

    case ScheduleTypes.BiAnnual:
      return values
        .map((val) => {
          const [month, day] = val.replace('M', '').split('-');
          return `${monthNames[Number(month) - 1]} ${ordinalSuffix(Number(day))}`;
        })
        .join(' and ');

    case ScheduleTypes.Quarterly:
      return values
        .map((val) => {
          const parts = val.split('-');

          if (val.startsWith('Q1') || val.startsWith('Q2') || val.startsWith('Q3') || val.startsWith('Q4')) {
            const [quarter, type, ...rest] = parts;
            if (type.startsWith('M') && rest[0]?.startsWith('W')) {
              // Q1-M1-W3-4
              const month = type.replace('M', '');
              const week = rest[0].replace('W', '');
              const day = rest[1];
              return `${getWeekName(week)} ${dayNames[Number(day)]} of ${monthNames[Number(month) - 1]} in ${quarter}`;
            } else if (type.startsWith('W')) {
              // Q1-W12-2
              const week = type.replace('W', '');
              const day = rest[0];
              return `${ordinalSuffix(Number(week))} week ${dayNames[Number(day)]} of ${quarter}`;
            } else if (type.startsWith('M')) {
              // Q1-M2-16
              const month = type.replace('M', '');
              const day = rest[0];
              return `${monthNames[Number(month) - 1]} ${ordinalSuffix(Number(day))} of ${quarter}`;
            }
          } else if (val.startsWith('Q-')) {
            const subParts = val.substring(2).split('-');

            if (subParts[0].startsWith('M') && subParts[1]?.startsWith('W')) {
              // Q-M1-W3-4
              const month = subParts[0].replace('M', '');
              const week = subParts[1].replace('W', '');
              const day = subParts[2];
              return `${getWeekName(week)} ${dayNames[Number(day)]} of ${monthNames[Number(month) - 1]} in every quarter`;
            } else if (subParts[0].startsWith('W')) {
              const week = subParts[0].replace('W', '');
              const day = subParts[1];
              return `${ordinalSuffix(Number(week))} week ${dayNames[Number(day)]} in every quarter`;
            } else if (subParts[0].startsWith('M')) {
              const month = subParts[0].replace('M', '');
              const day = subParts[1];
              return `${monthNames[Number(month) - 1]} ${ordinalSuffix(Number(day))} in every quarter`;
            }
          }

          return val;
        })
        .join(', ');

    default:
      return '-';
  }
};

export const getScheduleColumns = (
  renderCell: (value: React.ReactNode) => string | number,
  renderOpenPositions: (renderedCellValue: React.ReactNode | IOpenPosition) => string,
  renderActionsCell: (row: any) => JSX.Element,
  t: (key: string) => string,
  filters: any[],
  handleFilterChange: (filterType: string, filterValue: string[], event?: React.MouseEvent<HTMLInputElement>) => void,
  isFiltersLoading: boolean,
  selectedJobNumber?: string[],
  selectedJobName?: string[],
  selectedJobAddress?: string[],
  selectedState?: string[],
  selectedCity?: string[],
  selectedVendor?: string[],
  selectedCustomer?: string[],
  selectedScheduleName?: string[],
  selectedVendorAdminName?: string[],
  selectedScheduleStartDate?: string[],
  selectedScheduleEndDate?: string[],
  selectedJobStartDate?: string[],
  selectedJobEndDate?: string[],
  selectedScheduleStartTime?: string[],
  selectedScheduleEndTime?: string[],
  selectedVendorAdminEmail?: string[],
  selectedCreatedOn?: string[],
  selectedWorkTicketNumber?: string[],
  setIsFilterPopUpOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  setFilterName?: React.Dispatch<React.SetStateAction<string>>,
  isLoading?: boolean,
  setIsTARequiredPopupOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  setEnableTimeAndAttendance?: React.Dispatch<React.SetStateAction<boolean>>,
  setTimeAndAttendanceData?: React.Dispatch<React.SetStateAction<ScheduleData | undefined>>
): MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[] => [
  {
    accessorKey: 'jobNumber',
    header: 'Job Number',
    enableColumnFilter: true,
    enableSorting: false,
    Header: () => (
      <ColumnHeader>
        <span>{t('vendor:jobNumber')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.JobNumber);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedJobNumber), isLoading),
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'jobName',
    header: 'Job Name',
    enableSorting: true,
    enableColumnFilter: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('vendor:jobName')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.JobName);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedJobName), isLoading),

    size: 180,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
    enableColumnActions: false,
    sortingFn: 'alphanumericCaseSensitive',
  },
  {
    accessorKey: 'jobAddress',
    header: 'Job Address',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('tableHeader:jobAddress')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.JobAddress);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedJobAddress), isLoading),

    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'city',
    header: 'City',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'stateCode',
    header: 'State',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => states[renderedCellValue as keyof typeof states] || renderedCellValue,
  },
  {
    accessorKey: 'jobStartDate',
    header: 'Job Start Date',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    filterFn: (row, _columnIds, filterValue) => {
      const formattedValue = moment(row.getValue('jobStartDate')).format(DateFormat.StandardDate);
      return formattedValue.toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ cell }) => formatDateCell(cell),
  },
  {
    accessorKey: 'jobEndDate',
    header: 'Job End Date',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    filterFn: (row, _columnIds, filterValue) => {
      const formattedValue = moment(row.getValue('jobEndDate')).format(DateFormat.StandardDate);
      return formattedValue.toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ cell }) => formatDateCell(cell),
  },
  {
    accessorKey: 'jobType',
    header: 'Job Type',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'customerName',
    header: 'Customer Name',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('tableHeader:customerName')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.Customer);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedCustomer), isLoading),

    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'vendorNumber',
    header: 'Vendor Number',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'vendorName',
    header: 'Vendor Name',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'vendorAdminName',
    header: 'Vendor Admin Name',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'vendorAdminEmail',
    header: 'Vendor Admin Email',
    size: 170,
    enableColumnActions: false,
    sortingFn: 'alphanumericCaseSensitive',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'scheduleName',
    header: 'Schedule Name',
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'scheduleDays',
    header: 'Schedule Days',
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      const scheduleDays = cell.getValue() as string[];
      const scheduleType = cell.row.original.mdScheduleType;
      return getReadableScheduleDays(scheduleType, scheduleDays);
    },
  },
  {
    accessorKey: 'mdScheduleType',
    header: 'Schedule Type',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'scheduleStatus',
    header: 'Schedule Status',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'scheduleStartTime',
    header: 'Schedule Start Time',
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => {
      const time = String(renderedCellValue);
      return time ? formatTimeTo12Hr(time) : '-';
    },
  },
  {
    accessorKey: 'scheduleEndTime',
    header: 'Schedule End Time',
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => {
      const time = String(renderedCellValue);
      return time ? formatTimeTo12Hr(time) : '-';
    },
  },
  {
    accessorKey: 'supervisorName',
    header: 'Supervisor Name',
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => {
      const supervisors = row.original.supervisors as { name: string | null | undefined }[];
      return (
        supervisors
          ?.filter((supervisor) => supervisor.name)
          .map((supervisor) => supervisor.name)
          .join(', ') || '-'
      );
    },
  },
  {
    accessorKey: 'supervisorEmail',
    header: 'Supervisor Email',
    enableSorting: false,
    enableColumnFilter: false,
    size: 400,
    Cell: ({ row }) => {
      const supervisors = row.original.supervisors as { email: string | null | undefined }[];
      return (
        supervisors
          ?.filter((supervisor) => supervisor.email)
          .map((supervisor) => supervisor.email)
          .join(', ') || '-'
      );
    },
  },
  {
    accessorKey: 'associates',
    header: 'Associate Name',
    enableSorting: false,
    enableColumnFilter: false,
    Cell: ({ row }) => {
      const associates = row.original.associates as { name: string | null | undefined }[];
      return (
        associates
          ?.filter((associate) => associate.name)
          .map((associate) => associate.name)
          .join(', ') || '-'
      );
    },
  },
  {
    accessorKey: 'associateEmail',
    header: 'Associate Email',
    enableSorting: false,
    enableColumnFilter: false,
    size: 400,
    Cell: ({ row }) => {
      const associates = row.original.associates as { email: string | null | undefined }[];
      return (
        associates
          ?.filter((associate) => associate.email)
          .map((associate) => associate.email)
          .join(', ') || '-'
      );
    },
  },
  {
    accessorKey: 'totalFTE',
    header: 'Number of Full Time Employees',
    enableSorting: false,
    enableColumnFilter: false,
    size: 180,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'noOfAssociatesAssigned',
    header: 'Number of Employees Assigned',
    enableSorting: false,
    enableColumnFilter: false,
    size: 180,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: 'openPositions',
    header: 'Open Positions',
    enableSorting: false,
    enableColumnFilter: false,
    size: 100,
    Cell: ({ renderedCellValue }) => {
      return renderOpenPositions(renderedCellValue);
    },
  },
  {
    accessorKey: 'createdOn',
    header: 'Created On',
    size: 100,
    filterFn: (row, _columnIds, filterValue) => {
      const formattedValue = moment(row.getValue('createdOn')).format(DateFormat.StandardDate);
      return formattedValue.toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ cell }) => formatDateCell(cell),
    enableColumnActions: false,
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: 'timeAndAttendanceRequired',
    header: 'is Routing Enabled',
    size: 100,
    Cell: ({ row }) => {
      const original = row?.original as any;

      const isEnabled = Boolean(original?.timeAndAttendanceRequired);

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Switch
            checked={isEnabled}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: theme.palette.primary.dark },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.primary.dark },
            }}
            onChange={() => {
              setIsTARequiredPopupOpen?.(true);
              setEnableTimeAndAttendance?.(!isEnabled);
              setTimeAndAttendanceData?.({
                jobName: original?.jobName,
                scheduleName: original?.scheduleName,
                woScheduleMasterId: original?.woScheduleMasterId,
                jobNumber:
                  original?.jobType === JobType.OneTime || original?.jobType === JobType.Emergency
                    ? original?.intReferenceId
                    : original?.intId,
              });
            }}
          />
        </Box>
      );
    },
    enableColumnActions: false,
    enableHiding: true,
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    header: 'Actions',
    enableColumnActions: false,
    enablePinning: false,
    size: 90,
    Cell: ({ row }) => renderActionsCell(row),
  },
];
